import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import {
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
  CartesianGrid,
  Area,
  AreaChart,
} from 'recharts';
import cn from 'classnames';
import IsCountingBlock from '../IsCountingBlock/IsCountingBlock';
import CustomizedAxisTick from './components/CustomizedAxisTick';
import CustomTooltip from './components/CustomTooltip';
import useRequestsQueryState from '../../hooks/useRequestsQueryState';
import useFilterState from '../../hooks/useFilterState';
import { filtersData } from '../../constants/filtersData';
import { filterNames } from '../../constants/filterNames';
import { getPlaceholderData } from './helpers/getPlaceholderData';
import styles from './RequestsLineChart.module.scss';
import { Icon } from 'antd';
import { ReactComponent as ChartModernCircle } from '../../../../resource/statistics/chartModernCircle.svg';

const RequestsLineChart = ({ className }) => {
  const [[fromDate, toDate]] = useFilterState(
    filtersData[filterNames.createDate]
  );

  const requestsByDate = useSelector(
    (state) => state.statisticsStore.chartsRequests.requestsByDate
  );

  const isCounting = useSelector((state) => state.statisticsStore.isCounting);

  const requestsQueryState = useRequestsQueryState();

  const noDataState = useMemo(
    () =>
      !requestsQueryState?.isFetching &&
      !isCounting &&
      requestsByDate?.length === 0,
    [requestsQueryState, isCounting, requestsByDate]
  );

  const placeholderData = useMemo(
    () => getPlaceholderData(fromDate, toDate),
    [fromDate, toDate]
  );

  return (
    <div
      className={cn(styles.root, className, {
        [styles.skeleton]: requestsQueryState?.isFetching,
      })}
    >
      <div
        className={cn({
          [styles.nonPointerEvents]:
            noDataState || requestsQueryState?.isFetching,
        })}
      >
        <IsCountingBlock spinning={isCounting}>
          {requestsQueryState?.isFetching ? (
            <div className={styles.titleSkeleton} />
          ) : (
            <div className={styles.header}>
              <span className={styles.anticon}>
                <Icon
                  className={styles.icon}
                  component={ChartModernCircle}
                />
              </span>
              <p className={styles.title}>Динамика заявок</p>
            </div>
          )}

          <ResponsiveContainer
            width="100%"
            height={342}
          >
            <AreaChart
              width={500}
              height={342}
              data={
                noDataState || requestsQueryState?.isFetching
                  ? placeholderData
                  : requestsByDate
              }
              margin={{
                top: 20,
                bottom: 20,
                right: 50,
                left: 0,
              }}
            >
              <defs>
                <linearGradient
                  id="RequestsLineChart"
                  x1="0"
                  y1="0"
                  x2="0"
                  y2="1"
                >
                  <stop
                    offset="5%"
                    stopColor="#3378F966"
                    stopOpacity={1}
                  />
                  <stop
                    offset="95%"
                    stopColor="#3378F900"
                    stopOpacity={1}
                  />
                </linearGradient>
              </defs>
              <XAxis
                axisLine={false}
                tickLine={false}
                padding={{ left: 15 }}
                dataKey="name"
                fontSize={12}
                tickMargin={20}
              />
              <YAxis
                axisLine={false}
                tickLine={false}
              />
              <Tooltip content={<CustomTooltip />} />
              <Area
                cursor="pointer"
                type="linear"
                dataKey="value"
                stroke={
                  noDataState || requestsQueryState?.isFetching
                    ? '#DED9D9'
                    : '#2F80ED'
                }
                fillOpacity={1}
                fill="url(#RequestsLineChart)"
              />
              <CartesianGrid
                vertical={false}
                stroke="#56CCF266"
              />
            </AreaChart>
          </ResponsiveContainer>
        </IsCountingBlock>
      </div>
      {noDataState && <div className={styles.placeholder}>Данных нет</div>}
    </div>
  );
};

export default RequestsLineChart;
